import { useCallback, useEffect, useRef, useState } from 'react';

/* Web Speech API 최소 타입 (lib.dom 에 없음) */
type SpeechAlt = { transcript: string };
type SpeechResult = { isFinal: boolean; 0: SpeechAlt; length: number };
type SpeechEvent = { results: { length: number; [i: number]: SpeechResult } };
type SpeechErrorEvent = { error: string };
interface SpeechRec {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onstart: (() => void) | null;
  onresult: ((e: SpeechEvent) => void) | null;
  onerror: ((e: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SpeechRecCtor = new () => SpeechRec;

const getCtor = (): SpeechRecCtor | undefined => {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { SpeechRecognition?: SpeechRecCtor; webkitSpeechRecognition?: SpeechRecCtor };
  return w.SpeechRecognition || w.webkitSpeechRecognition;
};

const SEND_EVERY_MS = 2000;
const IDLE_MS = 5000;
const rid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

/**
 * 자막 말하기: 한 번 누르면 인식 시작, 다시 누르면 완료.
 * 진행 중 텍스트를 최대 2초마다(바뀐 경우만) onUpdate(final:false).
 * 완료(다시 누르기·5초간 새 입력 없음) 시 onUpdate(final:true).
 */
export function usePushToTalk(onUpdate: (id: string, text: string, final: boolean) => void) {
  const Ctor = getCtor();
  const supported = !!Ctor;
  const [holding, setHolding] = useState(false);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const recRef = useRef<SpeechRec | null>(null);
  const heldRef = useRef(false);
  const idRef = useRef('');
  const doneRef = useRef(''); // 이전 인식 세션(자동 재시작 전)들의 확정 텍스트
  const segRef = useRef(''); // 현재 세션 확정
  const interimRef = useRef('');
  const lastSentRef = useRef('');
  const timerRef = useRef<number | null>(null);
  const idleRef = useRef<number | null>(null);
  const stopRef = useRef<() => void>(() => {});
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const current = () => `${doneRef.current} ${segRef.current} ${interimRef.current}`.replace(/\s+/g, ' ').trim();

  const clearIdle = () => {
    if (idleRef.current != null) window.clearTimeout(idleRef.current);
    idleRef.current = null;
  };
  const armIdle = () => {
    clearIdle();
    idleRef.current = window.setTimeout(() => {
      idleRef.current = null;
      stopRef.current();
    }, IDLE_MS);
  };

  const startRec = useCallback(() => {
    if (!Ctor) return;
    const r = new Ctor();
    r.lang = 'ko-KR';
    r.interimResults = true;
    r.continuous = true;
    r.onstart = () => {
      if (recRef.current !== r || !heldRef.current) return;
      if (idleRef.current == null) armIdle();
    };
    r.onresult = (e) => {
      if (recRef.current !== r) return;
      let fin = '';
      let int = '';
      for (let i = 0; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) fin += `${res[0].transcript} `;
        else int += res[0].transcript;
      }
      segRef.current = fin;
      interimRef.current = int;
      if (heldRef.current) {
        setPreview(current());
        armIdle();
      }
    };
    r.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setError('마이크 권한이 거부됐어요. 브라우저 주소창의 사이트 설정에서 마이크를 허용해 주세요.');
        stopRef.current();
      } else if (e.error === 'audio-capture') {
        setError('마이크를 찾지 못했어요.');
      } else if (e.error === 'network') {
        setError('음성 인식 서버에 연결하지 못했어요. 네트워크를 확인해 주세요.');
      }
      /* no-speech · aborted 는 무시(듣는 중이면 onend 에서 재시작) */
    };
    r.onend = () => {
      if (recRef.current !== r) return;
      doneRef.current = `${doneRef.current} ${segRef.current} ${interimRef.current}`;
      segRef.current = '';
      interimRef.current = '';
      if (heldRef.current) {
        try {
          startRec();
        } catch {
          /* ignore */
        }
      } else {
        recRef.current = null;
      }
    };
    recRef.current = r;
    try {
      r.start();
    } catch {
      /* 이미 시작됨 등 */
    }
  }, [Ctor]);

  const start = useCallback(() => {
    if (heldRef.current) return;
    if (!Ctor) {
      setError('이 브라우저는 음성 인식을 지원하지 않아요 (Chrome/Safari 사용)');
      return;
    }
    setError('');
    heldRef.current = true;
    idRef.current = rid();
    doneRef.current = '';
    segRef.current = '';
    interimRef.current = '';
    lastSentRef.current = '';
    setPreview('');
    setHolding(true);
    startRec();
    timerRef.current = window.setInterval(() => {
      const t = current();
      if (t && t !== lastSentRef.current) {
        lastSentRef.current = t;
        onUpdateRef.current(idRef.current, t, false);
      }
    }, SEND_EVERY_MS);
  }, [Ctor, startRec]);

  const stop = useCallback(() => {
    if (!heldRef.current) return;
    heldRef.current = false;
    setHolding(false);
    clearIdle();
    if (timerRef.current != null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    const t = current();
    setPreview(t);
    if (t) onUpdateRef.current(idRef.current, t, true);
    const r = recRef.current;
    recRef.current = null; // 늦게 오는 결과 무시
    try {
      r?.stop();
    } catch {
      /* ignore */
    }
  }, []);
  stopRef.current = stop;

  useEffect(
    () => () => {
      heldRef.current = false;
      if (timerRef.current != null) window.clearInterval(timerRef.current);
      if (idleRef.current != null) window.clearTimeout(idleRef.current);
      try {
        recRef.current?.abort();
      } catch {
        /* ignore */
      }
      recRef.current = null;
    },
    []
  );

  return { supported, holding, preview, error, start, stop };
}
